/**
 * Mobile Language Switcher FAB v8.0
 * Fixed floating button for easy language switching on mobile
 * 
 * Uses unified LocaleManager for consistent behavior.
 */
import React, { useState } from 'react';
import { useLanguage } from '@/lib/context';
import { SUPPORTED_LOCALES, getLocaleInfo } from '@/i18n';

export default function MobileLanguageFAB() {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = getLocaleInfo(language);

  const handleSelect = (code) => {
    setLanguage(code);
    setIsOpen(false);
  };

  // Sort with current language first
  const sortedLocales = [...SUPPORTED_LOCALES].sort((a, b) => {
    if (a.code === language) return -1;
    if (b.code === language) return 1;
    return a.nativeLabel.localeCompare(b.nativeLabel);
  });

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Language options */}
      {isOpen && (
        <div className="fixed bottom-36 right-4 z-50 bg-white rounded-xl shadow-xl p-2 space-y-1 md:hidden max-h-[60vh] overflow-y-auto">
          {sortedLocales.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left
                ${language === lang.code ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50'}
              `}
              data-testid={`mobile-lang-${lang.code}`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="font-medium">{lang.nativeLabel}</span>
            </button>
          ))}
        </div>
      )}

      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="mobile-lang-switcher md:hidden"
        aria-label="Change language"
        data-testid="mobile-language-fab"
      >
        <span className="text-xl">{currentLang?.flag || '🌐'}</span>
      </button>
    </>
  );
}
